import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Circle } from "lucide-react";
import { DynamicIcon } from "./IconPicker";

export type ColorOption = {
  value: string;
  hexColor: string;
  icon: string | null;
  label: string;
};

interface ColorTagPickerProps {
  value: string | null;
  colors: ColorOption[];
  onChange: (next: string | null) => void;
}

const POPOVER_GAP = 6;

export const ColorTagPicker: React.FC<ColorTagPickerProps> = ({ value, colors, onChange }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const current = colors.find((c) => c.value === value);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current || !popoverRef.current) return;
    const btnRect = buttonRef.current.getBoundingClientRect();
    const popRect = popoverRef.current.getBoundingClientRect();

    let top = btnRect.top - popRect.height - POPOVER_GAP;
    if (top < 8) top = btnRect.bottom + POPOVER_GAP;

    let left = btnRect.left + btnRect.width / 2 - popRect.width / 2;
    const maxLeft = window.innerWidth - popRect.width - 8;
    if (left < 8) left = 8;
    if (left > maxLeft) left = maxLeft;

    setPos({ top, left });
  }, [open, colors.length]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onReflow = () => setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    };
  }, [open]);

  const pick = (next: string | null) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className="p-1.5 rounded-md transition-all text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
        title={current?.label ?? "Color Tag"}
      >
        {current ? (
          <div
            className="w-5 h-5 rounded-full flex items-center justify-center"
            style={{ backgroundColor: current.hexColor }}
          >
            {current.icon && (
              <DynamicIcon name={current.icon} size={12} className="text-white" />
            )}
          </div>
        ) : (
          <Circle size={20} />
        )}
      </button>

      {open &&
        createPortal(
          <div
            ref={popoverRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              visibility: pos ? "visible" : "hidden",
            }}
            className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg p-2 flex gap-1 z-[100]"
          >
            {colors.map((color) => (
              <button
                key={color.value}
                onClick={(e) => {
                  e.stopPropagation();
                  pick(value === color.value ? null : color.value);
                }}
                className={`w-6 h-6 rounded-full transition-transform hover:scale-125 flex items-center justify-center ${
                  value === color.value ? "ring-2 ring-offset-1 scale-110" : ""
                }`}
                style={{
                  backgroundColor: color.hexColor,
                  ...(value === color.value ? { ringColor: color.hexColor } : {}),
                }}
                title={color.label}
              >
                {color.icon && (
                  <DynamicIcon name={color.icon} size={12} className="text-white" />
                )}
              </button>
            ))}
            {value && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  pick(null);
                }}
                className="w-6 h-6 rounded-full border border-dashed border-slate-400 text-[10px] text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                title="Clear"
              >
                ✕
              </button>
            )}
          </div>,
          document.body,
        )}
    </>
  );
};
